/**
 * Drives the app update gate:
 *  - status 'hard' → renders the blocking <UpdateGate /> over the whole app.
 *  - status 'soft' → fires a dismissible "update available" dialog, throttled so
 *    it isn't shown on every launch.
 *  - status 'ok'   → renders nothing.
 *
 * Mounted once near the root layout, inside QueryClientProvider.
 */

import { useEffect, useRef } from 'react';
import { useAppVersionCheck } from '../../hooks/useAppVersionCheck';
import { useDialog } from '../../hooks/useDialog';
import {
  shouldShowUpdatePrompt,
  markUpdatePromptDismissed,
} from '../../helpers/updatePromptThrottle.helper';
import { openAppStore } from '../../helpers/openAppStore.helper';
import { UpdateGate } from './UpdateGate';

const SOFT_PROMPT_DEFAULT_MESSAGE =
  'A new version of Zporti is available with the latest features and fixes.';

export function UpdateGateHost() {
  const { data } = useAppVersionCheck();
  const { showDialog } = useDialog();
  const softHandledRef = useRef(false);

  const status = data?.status ?? 'ok';
  const recommendedVersion = data?.recommendedVersion ?? null;
  const platform = data?.platform ?? null;
  const message = data?.message ?? null;

  useEffect(() => {
    if (status !== 'soft' || !recommendedVersion || !platform) return;
    if (softHandledRef.current) return;
    softHandledRef.current = true;

    let cancelled = false;
    void (async () => {
      const show = await shouldShowUpdatePrompt(recommendedVersion);
      if (!show || cancelled) return;

      await markUpdatePromptDismissed(recommendedVersion);
      const confirmed = await showDialog({
        title: 'Update available',
        message: message ?? SOFT_PROMPT_DEFAULT_MESSAGE,
        confirmText: 'Update Now',
        cancelText: 'Later',
      });
      if (confirmed && !cancelled) {
        void openAppStore(platform);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, recommendedVersion, platform, message, showDialog]);

  if (status === 'hard' && platform) {
    return <UpdateGate platform={platform} message={message} />;
  }
  return null;
}
